import React, { useEffect, useContext, useState } from 'react'
import * as yup from 'yup'
import { GridItem } from '@zuto/core/src/grid'
import { useCallbackOnce } from '@zuto/utilities'
import QuestionKey from '../../../../Questions/question-key'
import AppFormQuestion from '../../../AppFormQuestion'
import { ApplicationFormContext } from '../../../ApplicationForm/ApplicationFormContext'
import withFade from '../../../HOC/withFade'
import NavigationButtons from '../../../NavigationButtons/NavigationButtons'
import EmploymentHistoryEntryContext from '../EmploymentEntry/EmploymentHistoryEntryContext'
import LengthOfTime from '../../../LengthOfTime'
import { EmploymentEntryErrors } from '../EmploymentHistoryContext'
import LengthOfTimeEntry, { buildLengthOfTimeEntry } from '../../../LengthOfTime/LengthOfTimeEntry'
import EmploymentHistoryValidator from '../../../../validators/EmploymentHistoryValidator'
import { questionFontStyle, fireReactGAEvent } from '../../utils'
import { HistoryIndex } from '../../HistoryEntry/HistoryEntry'
import { PrefillReviewPageApplicationFormContext, YearsMonthsDuration } from '../../../ApplicationForm/PrefillReviewPageApplicationFormContext'
import { calculateTotalMonths } from '../../../../utils/utils'
import { UpdateFollowingPrefillStepType } from '../../../../Questions/question-component-props'
import { useAppFormEventsService } from '../../../../hooks/useAppFormEventsService'

interface TimeInEmploymentSituationProps {
  existingDuration?: LengthOfTimeEntry
  entryId: HistoryIndex
  disablePrevious?: boolean
  situation: string | undefined
  employer?: string
  updateFollowingPrefillStep?: UpdateFollowingPrefillStepType
}

interface EmploymentDuration {
  [entryId: string]: YearsMonthsDuration
}

type Errors = {
  message?: string
  fields: string[]
}

const TimeInEmploymentSituation = ({ existingDuration, entryId, disablePrevious = false, situation, employer, updateFollowingPrefillStep }: TimeInEmploymentSituationProps): JSX.Element => {
  const appFormContext = useContext(ApplicationFormContext)
  const { featureSwitches } = appFormContext
  const eventsService = useAppFormEventsService()
  const { reviewPageV3 } = featureSwitches ?? {}
  const { dispatch } = useContext(EmploymentHistoryEntryContext)
  const { dispatch: appFormContextDispatch } = useContext(ApplicationFormContext)
  const { employmentDuration, setEmploymentDuration, hasNavigatedFromRequiredClick, setHasNavigatedFromRequiredClick } = useContext(PrefillReviewPageApplicationFormContext)
  const [years, setYears] = useState<string | undefined>(existingDuration?.years)
  const [months, setMonths] = useState<string | undefined>(existingDuration?.months)
  const [nextButtonCb, resetNextButtonCb] = useCallbackOnce()
  const [previousButtonCb] = useCallbackOnce()
  const [errors, setErrors] = useState<EmploymentEntryErrors>({})

  const lengthOfTimeValidator = EmploymentHistoryValidator.LengthOfTimeValidator().getValidator()
  const sumOfEmploymentDuration = calculateTotalMonths(employmentDuration)
  const reviewPageV3AndClickedRequired = reviewPageV3 && hasNavigatedFromRequiredClick
  const has24MonthsEmploymentHistoryAndNotClickedRequired = sumOfEmploymentDuration >= 24 && !hasNavigatedFromRequiredClick

  const lengthOfTimeErrors = (): Errors => {
    const monthsError = errors?.months
    const yearsError = errors?.years
    const durationError = errors?.duration
    const errorObject: Errors = {
      message: yearsError || monthsError || durationError,
      fields: []
    }
    if (monthsError) {
      errorObject.fields.push('months')
    }
    if (yearsError) {
      errorObject.fields.push('years')
    }
    if (durationError) {
      errorObject.fields.push('duration')
    }

    return errorObject
  }

  const validate = (): boolean => {
    const schema = lengthOfTimeValidator

    const objectToValidate = {
      years,
      months
    }

    try {
      schema.validateSync(objectToValidate, {
        abortEarly: false
      })
    } catch (e: unknown) {
      const err = e as yup.ValidationError
      const prettyErrors: Record<string, string> = {}
      if (!err.inner) return false
      err.inner.forEach((validationError) => {
        if (!validationError.path) return
        const [errorMessage] = validationError.errors
        prettyErrors[validationError.path.toString()] = errorMessage
        fireReactGAEvent({
          category: 'Application Form',
          action: 'Validation Error',
          label: validationError.path.toString(),
          nonInteraction: true
        })
      })
      setErrors(prettyErrors)
      return false
    }

    return true
  }

  useEffect(() => {
    const label = `qst.employmentHistory.${parseInt(entryId, 10) - 1}.situation`
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Question Visible',
      label,
      nonInteraction: true
    })
    eventsService?.sendEvent('AppFormQuestionVisible', { QuestionLabel: label })

    const updatedDuration = {
      years: Number(years) || 0,
      months: Number(months) || 0
    }

    if (updateFollowingPrefillStep) {
      setEmploymentDuration((prevDuration: { [entryId: string]: YearsMonthsDuration }) => {
        /*
          Filter out entries that are not up to the current entryId so that we can reach previous
          entry any additional time editing. (Used for navigation purposes only)
        */
        const filteredEntries: EmploymentDuration = Object.keys(prevDuration)
          .filter((key) => key <= entryId)
          .reduce((obj: EmploymentDuration, key: string) => {
            const newObj: EmploymentDuration = { ...obj }
            newObj[key] = prevDuration[key]
            return newObj
          }, {})

        const updatedEntries = { ...filteredEntries, [entryId]: updatedDuration }
        return updatedEntries
      })
    }
  }, [])

  const nextHandler = (): void => {
    if (!validate()) {
      resetNextButtonCb()
      return
    }

    dispatch({
      type: 'UPDATE_TIME_IN_SITUATION',
      value: buildLengthOfTimeEntry(years, months)
    })

    dispatch({
      type: 'NEXT'
    })

    setTimeout(() => {
      if (updateFollowingPrefillStep) {
        if (reviewPageV3AndClickedRequired || has24MonthsEmploymentHistoryAndNotClickedRequired) {
          updateFollowingPrefillStep(true, true, false)
        }
      }
    }, 50)

    if (reviewPageV3) {
      setHasNavigatedFromRequiredClick(false)
    }
  }

  const previousHandler = (): void => {
    appFormContextDispatch({
      type: 'NAVIGATED_FROM_QUESTION',
      questionName: `empSituationDuration-${parseInt(entryId, 10)}`,
      lastNavAction: 'PREVIOUS'
    })
    dispatch({
      type: 'UPDATE_TIME_IN_SITUATION',
      value: buildLengthOfTimeEntry(years, months)
    })
    dispatch({
      type: 'PREVIOUS'
    })
  }

  const valueChanged = (update: Record<string, unknown>): void => {
    const updatedDuration = {
      years,
      months
    }

    Object.keys(update).forEach((key) => {
      if (key === 'years') {
        updatedDuration.years = update.years as string
      }
      if (key === 'months') {
        updatedDuration.months = update.months as string
      }
    })

    setYears(updatedDuration.years)
    setMonths(updatedDuration.months)

    if (updateFollowingPrefillStep) {
      // TODO: Create a util function or custom hook to contain the logic here
      setEmploymentDuration((prevDuration) => {
        const updatedEntries = { ...prevDuration }
        const currentEntry = updatedEntries[entryId]

        /*
          We want to remove all entries after the current entryId if
          lengthOfTime is edited again. (Used for navigation purposes only)
        */
        if (currentEntry && (currentEntry.years !== Number(updatedDuration.years) || currentEntry.months !== Number(updatedDuration.months))) {
          const entryKeys = Object.keys(updatedEntries)
          const currentEntryIndex = entryKeys.indexOf(entryId)
          const entriesToRemove = entryKeys.slice(currentEntryIndex + 1)

          entriesToRemove.forEach((entryToRemove) => {
            delete updatedEntries[entryToRemove]
          })
        }

        updatedEntries[entryId] = {
          years: Number(updatedDuration.years) || 0,
          months: Number(updatedDuration.months) || 0
        }

        return updatedEntries
      })
    }
  }

  const handleEnterPress = (event: React.KeyboardEvent): void => {
    if (event.code === 'Enter' || event.key === 'Enter') {
      nextButtonCb(nextHandler)
    }
  }

  const getQuestionForSituation = (): string => {
    switch (situation) {
      case 'self employed':
      case 'retired':
        return `${situation}`
      case 'armed forces':
        return 'in the armed forces'
      case 'carer':
      case 'student':
      case 'homemaker':
        return `a ${situation}`
      case 'disability':
        return 'on disability living allowance'
      case 'not employed':
        return 'unemployed'
      default:
        return 'in this situation'
    }
  }

  const buildQuestion = (): string => {
    if (employer && !(situation === 'self employed' || situation === 'armed forces')) {
      return `How long ${entryId === '1' ? 'have you worked' : 'did you work'} at ${employer}?`
    }
    return `How long ${entryId === '1' ? 'have you been' : 'were you'} ${getQuestionForSituation()}?`
  }

  return (
    <>
      <GridItem
        gridColumn={['auto / span 12', '3 / span 8']}
        minHeight="174px"
      >
        <LengthOfTime
          valueChanged={valueChanged}
          onKeyPress={handleEnterPress}
          defaultValue={existingDuration}
          error={lengthOfTimeErrors()}
          trackingLabel={`qst.employmentHistory.${parseInt(entryId, 10) - 1}.timeAtJob`}
        >
          <AppFormQuestion
            mt={3}
            mb={3}
            questionKey={`${QuestionKey.EMPLOYMENT_HISTORY}.lengthOfTime`}
            textStyle={questionFontStyle}
            data-hj-suppress=""
          >
            {buildQuestion()}
          </AppFormQuestion>
        </LengthOfTime>
      </GridItem>
      <NavigationButtons
        nextHandler={() => nextButtonCb(nextHandler)}
        previousHandler={() => previousButtonCb(previousHandler)}
        disableNext={!(months || years)}
        disablePrevious={disablePrevious ? 'invisible' : undefined}
      />
    </>
  )
}

export default withFade(TimeInEmploymentSituation)
